import { useEffect, useState, useMemo } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { EditorProvider, useEditor } from './contexts/EditorContext';
import { settingsApi } from './services/api';
import { parseUrlParams, UrlParams, LayerOverride } from './utils/urlSync';
import MapEditor from './menus/MapEditor';
import './App.css';

/**
 * Main application content component
 * Handles conditional rendering based on editor mode
 * Reads URL params on startup to allow sharing links to specific map locations
 * @returns The rendered application content
 */
function AppContent(): JSX.Element {
  const { isEditorMode, selectedMapId, selectMap } = useEditor();
  const [urlParamsLoaded, setUrlParamsLoaded] = useState<boolean>(false);

  /**
   * Parse URL params once on mount
   * Memoized to ensure stable reference
   */
  const initialUrlParams = useMemo<UrlParams>(() => parseUrlParams(), []);

  /**
   * Initial viewport from URL params (if provided)
   * Used to set the starting position when opening a shared link
   */
  const initialViewport = useMemo(() => {
    if (initialUrlParams.x !== null && initialUrlParams.y !== null && initialUrlParams.zoom !== null) {
      return {
        x: initialUrlParams.x,
        y: initialUrlParams.y,
        zoom: initialUrlParams.zoom
      };
    }
    return undefined;
  }, [initialUrlParams]);

  /**
   * Initial layer overrides from URL params
   * Used to apply visibility and opacity overrides from shared links
   */
  const initialLayerOverrides = useMemo<LayerOverride[]>(() => {
    return initialUrlParams.layerOverrides;
  }, [initialUrlParams]);

  /**
   * Initial description panel layer ID from URL params
   */
  const initialDescriptionLayerId = useMemo<string | null>(() => {
    return initialUrlParams.descriptionLayerId;
  }, [initialUrlParams]);

  /**
   * Hide UI flag from URL params
   * When true, only the map viewer is shown without any interface elements
   */
  const hideUi = useMemo<boolean>(() => {
    return initialUrlParams.hideUi;
  }, [initialUrlParams]);

  /**
   * Load map from URL params or fall back to default map
   * URL params take priority over default map settings
   */
  useEffect(() => {
    if (urlParamsLoaded) return;

    // If URL has a map param, use it
    if (initialUrlParams.mapId) {
      selectMap(initialUrlParams.mapId);
      setUrlParamsLoaded(true);
      return;
    }

    // Otherwise, load default map when in view mode
    if (!isEditorMode && !selectedMapId) {
      settingsApi.get()
        .then(res => {
          if (res.settings.defaultMapId) {
            selectMap(res.settings.defaultMapId);
          }
        })
        .catch(err => {
          console.error('Failed to load default map:', err);
        })
        .finally(() => {
          setUrlParamsLoaded(true);
        });
    } else {
      setUrlParamsLoaded(true);
    }
  }, [isEditorMode, selectedMapId, selectMap, initialUrlParams.mapId, urlParamsLoaded]);

  return (
    <div className="app">
      <main className="app-main">
        {selectedMapId ? (
          <MapEditor
            mapId={selectedMapId}
            readOnly={!isEditorMode}
            onSelectMap={selectMap}
            initialViewport={initialViewport}
            initialLayerOverrides={initialLayerOverrides}
            initialDescriptionLayerId={initialDescriptionLayerId}
            hideUi={hideUi}
          />
        ) : (
          <span className="no-map-message">No map selected</span>
        )}
      </main>
    </div>
  );
}

/**
 * Root application component for MapBuilder
 * Serves as the main entry point for the React application
 * Wraps the app with authentication and editor providers
 * @returns The rendered application component
 */
function App(): JSX.Element {
  return (
    <AuthProvider>
      <EditorProvider>
        <AppContent />
      </EditorProvider>
    </AuthProvider>
  );
}

export default App;
